import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from "@react-google-maps/api";
import { MapPin, ExternalLink, DollarSign, Wifi, Monitor } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Custom red color scale for offline days (dark red = low offline, light red = high offline)
const getMarkerColor = (offlineDays) => {
  const days = Math.min(Math.max(offlineDays, 2), 300);
  const colorScale = [
    { threshold: 2, color: "#7f0000" },   // dark red (2 days)
    { threshold: 30, color: "#b30000" },
    { threshold: 90, color: "#d7301f" },
    { threshold: 180, color: "#ef6548" },
    { threshold: 250, color: "#fc8d59" },
    { threshold: 300, color: "#fdbb84" }, // light red (>=300 days)
  ];
  
  for (let i = colorScale.length - 1; i >= 0; i--) {
    if (days >= colorScale[i].threshold) {
      return colorScale[i].color;
    }
  }
  return "#7f0000";
};

// Calculate marker size based on revenue (log scale)
const getMarkerSize = (revenue, minSize = 8, maxSize = 24) => {
  if (!revenue || revenue <= 0) return minSize;
  // Log scale for revenue
  const scaled = Math.log1p(revenue);
  // Normalize to size range (log of ~500 to log of ~10000)
  const normalizedSize = Math.min(maxSize, Math.max(minSize, minSize + (scaled / 10) * (maxSize - minSize)));
  return Math.round(normalizedSize);
};

// Create custom marker icon SVG
const createMarkerIcon = (color, size) => {
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="${size * 2}" height="${size * 2}" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" fill="${color}" stroke="#ffffff" stroke-width="2" opacity="0.9"/>
      </svg>
    `)}`,
    scaledSize: { width: size * 2, height: size * 2 },
    anchor: { x: size, y: size },
  };
};

const mapContainerStyle = {
  width: "100%",
  height: "720px",
  borderRadius: "12px",
};

const defaultCenter = {
  lat: 39.8283, // Center of USA
  lng: -98.5795,
};

const mapOptions = {
  mapTypeControl: true,
  streetViewControl: false,
  fullscreenControl: true,
  zoomControl: true,
  scrollwheel: true,
  gestureHandling: "greedy",
  styles: [
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
  ],
};

export default function OfflineMap({ data, mostRecentDateStr }) {
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [map, setMap] = useState(null);
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    id: "google-map-script",
  });

  // Process data for map - filter to most recent date and only offline with valid coordinates
  const mapData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Filter to most recent date if specified
    let filtered = data;
    if (mostRecentDateStr) {
      filtered = data.filter((d) => d.Date === mostRecentDateStr);
    }

    // Filter to offline only and with valid coordinates
    return filtered
      .filter((d) => {
        const lat = parseFloat(d.Latitude);
        const lng = parseFloat(d.Longitude);
        return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
      })
      .map((d) => {
        const revenue = parseFloat(d["Last 2 Month Revenue"]) || 0;
        const offlineDays = Math.max(2, Math.min(300, parseInt(d["Offline Days"]) || 2));
        
        return {
          id: d["Venue ID"] || d["Serial Number"] || Math.random().toString(),
          lat: parseFloat(d.Latitude),
          lng: parseFloat(d.Longitude),
          address: d.Address || "",
          storeName: d["Store Name"] || "",
          serialNumber: d["Serial Number"] || "",
          networkType: d["Network Type"] || "Unknown",
          osVersion: d["OS Version"] || "Unknown",
          offlineDays: offlineDays,
          revenue: revenue,
          venueId: d["Venue ID"] || "",
          city: d.City || "",
          state: d.State || "",
          pa: d["PA Title"] || "",
          fa: d["FA Title"] || "",
        };
      });
  }, [data, mostRecentDateStr]);

  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    mapRef.current = mapInstance;
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
    mapRef.current = null;
  }, []);

  // Handle marker click - zoom to location
  const handleMarkerClick = useCallback((marker) => {
    setSelectedMarker(marker);
    if (mapRef.current) {
      mapRef.current.panTo({ lat: marker.lat, lng: marker.lng });
      mapRef.current.setZoom(14);
    }
  }, []);

  // Close info window
  const handleInfoWindowClose = useCallback(() => {
    setSelectedMarker(null);
  }, []);

  // Open logs page for DD Code
  const openLogsForDD = (ddCode) => {
    window.open(`/logs?dd=${encodeURIComponent(ddCode)}`, "_blank");
  };

  // Fit bounds to show all markers when data changes
  useEffect(() => {
    if (map && mapData.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      mapData.forEach((marker) => {
        bounds.extend({ lat: marker.lat, lng: marker.lng });
      });
      map.fitBounds(bounds, { padding: 50 });
      
      // Don't zoom in too much
      const listener = map.addListener("idle", () => {
        if (map.getZoom() > 15) {
          map.setZoom(15);
        }
        window.google.maps.event.removeListener(listener);
      });
    }
  }, [map, mapData]);

  if (loadError) {
    return (
      <div className="dolphin-card p-8 text-center">
        <p className="text-red-500">Error loading Google Maps. Please check your API key.</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="dolphin-card p-8 flex items-center justify-center h-[720px]">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-slate-200 border-t-[#006994] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500">Loading map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dolphin-card overflow-hidden" data-testid="offline-map-container">
      {/* Map Header */}
      <div className="p-5 border-b border-slate-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
              Offline Device Health Map
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              {mapData.length} offline devices with location data
            </p>
          </div>
        </div>
        
        {/* Legend Row */}
        <div className="flex items-center gap-10 bg-slate-50 rounded-lg p-3">
          {/* Offline Days Color Scale */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 font-medium whitespace-nowrap">Offline Days:</span>
            <div className="flex flex-col">
              {/* Color bar with gradient */}
              <div 
                className="h-4 w-48 rounded"
                style={{ 
                  background: "linear-gradient(to right, #7f0000 0%, #b30000 20%, #d7301f 40%, #ef6548 60%, #fc8d59 80%, #fdbb84 100%)"
                }}
              ></div>
              {/* Tick labels */}
              <div className="flex justify-between w-48 text-xs text-slate-500 mt-1">
                <span>2</span>
                <span>30</span>
                <span>90</span>
                <span>180</span>
                <span>&gt;300</span>
              </div>
            </div>
          </div>
          
          {/* Divider */}
          <div className="w-px h-10 bg-slate-300"></div>
          
          {/* Marker Size Legend */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 font-medium whitespace-nowrap">Revenue:</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Low</span>
              <div className="w-2 h-2 rounded-full bg-slate-400 border border-slate-500"></div>
              <div className="w-3 h-3 rounded-full bg-slate-400 border border-slate-500"></div>
              <div className="w-4 h-4 rounded-full bg-slate-400 border border-slate-500"></div>
              <div className="w-6 h-6 rounded-full bg-slate-400 border border-slate-500"></div>
              <span className="text-xs text-slate-500">High</span>
            </div>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="relative">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={defaultCenter}
          zoom={4}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={mapOptions}
        >
          {mapData.map((marker) => (
            <Marker
              key={marker.id}
              position={{ lat: marker.lat, lng: marker.lng }}
              onClick={() => handleMarkerClick(marker)}
              icon={createMarkerIcon(
                getMarkerColor(marker.offlineDays),
                getMarkerSize(marker.revenue)
              )}
              title={`${marker.storeName} - ${marker.offlineDays} days offline`}
            />
          ))}

          {selectedMarker && (
            <InfoWindow
              position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
              onCloseClick={handleInfoWindowClose}
              options={{ maxWidth: 360 }}
            >
              <div className="p-2 min-w-[280px]">
                {/* Header */}
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <h4 className="font-semibold text-slate-900 text-base">
                      {selectedMarker.storeName || "Unknown Store"}
                    </h4>
                    <p className="text-sm text-[#006994] font-medium">
                      {selectedMarker.venueId}
                    </p>
                  </div>
                  <Badge
                    className="text-white shrink-0"
                    style={{ backgroundColor: getMarkerColor(selectedMarker.offlineDays) }}
                  >
                    {selectedMarker.offlineDays} days
                  </Badge>
                </div>

                {/* Address */}
                <p className="text-sm text-slate-600 mb-3 flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                  <span>{selectedMarker.address}</span>
                </p>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Monitor className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">
                      SN: <span className="font-medium">{selectedMarker.serialNumber}</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Wifi className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">{selectedMarker.networkType}</span>
                  </div>
                  <div className="flex items-center gap-2 col-span-2">
                    <span className="text-slate-500">OS:</span>
                    <span className="font-medium">v{selectedMarker.osVersion}</span>
                  </div>
                </div>

                {/* Revenue */}
                <div className="flex items-center justify-between p-2 bg-red-50 rounded-lg mb-3">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-red-500" />
                    <span className="text-sm text-slate-600">Revenue Impact (Last 2 Mo)</span>
                  </div>
                  <span className="font-bold text-red-600">
                    ${selectedMarker.revenue.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>

                {/* PA/FA Info */}
                {(selectedMarker.pa || selectedMarker.fa) && (
                  <div className="text-xs text-slate-500 mb-3">
                    {selectedMarker.pa && <p>PA: {selectedMarker.pa}</p>}
                    {selectedMarker.fa && <p>FA: {selectedMarker.fa}</p>}
                  </div>
                )}

                {/* Action Button */}
                <Button
                  size="sm"
                  className="w-full bg-[#006994] hover:bg-[#005577] text-white"
                  onClick={() => openLogsForDD(selectedMarker.venueId)}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View Service Logs
                </Button>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>

        {/* Map Stats Overlay */}
        {mapData.length > 0 && (
          <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 border border-slate-200">
            <div className="flex items-center gap-4 text-sm">
              <div>
                <span className="text-slate-500">Devices:</span>
                <span className="font-bold text-slate-900 ml-1">{mapData.length}</span>
              </div>
              <div className="w-px h-4 bg-slate-200"></div>
              <div>
                <span className="text-slate-500">Revenue at Risk:</span>
                <span className="font-bold text-red-600 ml-1">
                  ${mapData.reduce((sum, m) => sum + m.revenue, 0).toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
